/**
 * 피벗 — 행 차원 × 열 차원 × 값(집계)을 새 Dataset 으로 만든다. 결과는 OneGrid 에 그대로 띄운다.
 *
 *   const p = pivot(ds, grid.getViewRows(), { rows: ['dept'], cols: ['labNm'], values: [{ col: 'rslt', agg: 'avg' }] })
 *   new OneGrid(el, { dataset: p.dataset, columns: p.columns, headerGroups: p.headerGroups, showFooter: true })
 *
 * 문자열 차원은 사전 코드로 묶으므로 100만 행도 한 번 훑는다.
 */
import { Dataset, StrColumn, NumColumn } from './dataset.js';
const SEP = '\u0001';
const collator = new Intl.Collator('ko', { numeric: true });
export function pivot(ds, view, spec) {
    const rowDims = spec.rows, colDims = spec.cols ?? [];
    const keyFn = (dims) => {
        const getters = dims.map(d => {
            const c = ds.cols[d];
            if (c instanceof StrColumn)
                return (r) => c.codes[r];
            return (r) => { const v = c.vals[r]; return v !== v ? -1 : v; };
        });
        return (r) => getters.map(g => g(r)).join(SEP);
    };
    const rk = keyFn(rowDims), ck = keyFn(colDims);
    const rowIdx = new Map(), colIdx = new Map();
    const rowFirst = [], colFirst = [];
    const V = spec.values.length;
    // acc[rowI][colI*V+v] = {s, n, mn, mx}
    const acc = [];
    const NF = 4; // sum, count, min, max
    for (let i = 0; i < view.length; i++) {
        const r = view[i];
        const a = rk(r), b = colDims.length ? ck(r) : '';
        let ri = rowIdx.get(a);
        if (ri === undefined) {
            ri = rowIdx.size;
            rowIdx.set(a, ri);
            rowFirst.push(r);
            acc.push(new Map());
        }
        let ci = colIdx.get(b);
        if (ci === undefined) {
            ci = colIdx.size;
            colIdx.set(b, ci);
            colFirst.push(r);
        }
        let cell = acc[ri].get(ci);
        if (!cell) {
            cell = new Float64Array(V * NF);
            for (let v = 0; v < V; v++) {
                cell[v * NF + 2] = Infinity;
                cell[v * NF + 3] = -Infinity;
            }
            acc[ri].set(ci, cell);
        }
        for (let v = 0; v < V; v++) {
            const pv = spec.values[v];
            const raw = ds.get(r, pv.col);
            if (pv.agg === 'count') {
                if (raw != null && raw !== '')
                    cell[v * NF + 1]++;
                continue;
            }
            const x = typeof raw === 'number' ? raw : NaN;
            if (x !== x)
                continue;
            const o = v * NF;
            cell[o] += x;
            cell[o + 1]++;
            if (x < cell[o + 2])
                cell[o + 2] = x;
            if (x > cell[o + 3])
                cell[o + 3] = x;
        }
    }
    // 열 키 정렬
    const colLabel = (r) => colDims.map(d => String(ds.get(r, d) ?? '(없음)')).join(' / ');
    const colOrderIdx = [...colIdx.values()].sort((x, y) => (spec.colOrder ?? collator.compare)(colLabel(colFirst[x]), colLabel(colFirst[y])));
    const colKeys = colOrderIdx.map(i => (colDims.length ? colLabel(colFirst[i]) : ''));
    const rowOrder = [...rowIdx.values()].sort((x, y) => {
        for (const d of rowDims) {
            const c = collator.compare(String(ds.get(rowFirst[x], d) ?? ''), String(ds.get(rowFirst[y], d) ?? ''));
            if (c)
                return c;
        }
        return 0;
    });
    const value = (cell, v) => {
        if (!cell)
            return null;
        const o = v * NF, agg = spec.values[v].agg;
        if (agg === 'count')
            return cell[o + 1];
        if (!cell[o + 1])
            return null;
        return agg === 'sum' ? cell[o] : agg === 'avg' ? cell[o] / cell[o + 1] : agg === 'min' ? cell[o + 2] : cell[o + 3];
    };
    // 결과 Dataset
    const outCols = rowDims.map(d => ({ id: d, type: ds.cols[d] instanceof NumColumn ? 'number' : 'string' }));
    const cid = (ci, v) => `c${ci}_${v}`;
    colOrderIdx.forEach((_ci, k) => spec.values.forEach((_, v) => outCols.push({ id: cid(k, v), type: 'number' })));
    if (spec.rowTotals !== false && colDims.length)
        spec.values.forEach((_, v) => outCols.push({ id: `tot_${v}`, type: 'number' }));
    const out = new Dataset(outCols, rowOrder.length);
    const data = {};
    for (const c of outCols)
        data[c.id] = new Array(rowOrder.length);
    rowOrder.forEach((ri, k) => {
        for (const d of rowDims)
            data[d][k] = ds.get(rowFirst[ri], d);
        colOrderIdx.forEach((ci, j) => spec.values.forEach((_, v) => { data[cid(j, v)][k] = value(acc[ri].get(ci), v); }));
        if (spec.rowTotals !== false && colDims.length) {
            spec.values.forEach((_pv, v) => {
                // 행 합계: 원자료 기준으로 다시 합친다 (평균의 평균 오류 방지)
                const merged = new Float64Array(NF);
                merged[2] = Infinity;
                merged[3] = -Infinity;
                for (const cell of acc[ri].values()) {
                    const o = v * NF;
                    merged[0] += cell[o];
                    merged[1] += cell[o + 1];
                    merged[2] = Math.min(merged[2], cell[o + 2]);
                    merged[3] = Math.max(merged[3], cell[o + 3]);
                }
                const fake = new Float64Array(V * NF);
                fake.set(merged, v * NF);
                data[`tot_${v}`][k] = value(fake, v);
            });
        }
    });
    out.loadColumnar({ n: rowOrder.length, data });
    // 그리드 컬럼·헤더
    const L = (id) => spec.labels?.[id] ?? id;
    const vLabel = (pv) => pv.label ?? `${L(pv.col)} ${{ sum: '합계', avg: '평균', count: '건수', min: '최소', max: '최대' }[pv.agg]}`;
    const columns = rowDims.map(d => ({ id: d, header: L(d), width: 120, pinned: true, sortable: true }));
    const headerGroups = [];
    colOrderIdx.forEach((_, k) => {
        const children = [];
        spec.values.forEach((pv, v) => {
            columns.push({ id: cid(k, v), header: V > 1 ? vLabel(pv) : colKeys[k] || vLabel(pv), width: 90, decimals: pv.agg === 'count' ? 0 : pv.decimals ?? (pv.agg === 'avg' ? 1 : undefined), format: 'number', summary: pv.agg === 'count' || pv.agg === 'sum' ? 'sum' : undefined });
            children.push(cid(k, v));
        });
        if (V > 1)
            headerGroups.push({ header: colKeys[k], children });
    });
    if (spec.rowTotals !== false && colDims.length) {
        const children = [];
        spec.values.forEach((pv, v) => { columns.push({ id: `tot_${v}`, header: V > 1 ? vLabel(pv) : '합계', width: 96, format: 'number', decimals: pv.agg === 'count' ? 0 : pv.decimals ?? (pv.agg === 'avg' ? 1 : undefined), summary: pv.agg === 'count' || pv.agg === 'sum' ? 'sum' : undefined }); children.push(`tot_${v}`); });
        if (V > 1)
            headerGroups.push({ header: '합계', children });
    }
    return { dataset: out, columns, headerGroups, colKeys, rowCount: rowOrder.length };
}
